import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import { FiArrowLeft } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import { trustMastersAPI } from '../services/api';
import { showSuccess, showError } from '../utils/toast';
import TrustMasterForm from '../components/TrustMasterForm';
import LoadingPage from '../components/LoadingPage';

/**
 * Trust Master Edit Page
 * Allows editing an existing trust master
 */
export default function TrustMasterEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [trustMaster, setTrustMaster] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrustMaster();
  }, [id]);

  const loadTrustMaster = async () => {
    try {
      setLoading(true);
      const response = await trustMastersAPI.getById(id);
      setTrustMaster(response.data);
    } catch (err) {
      console.error('Error loading trust master:', err);
      showError('Failed to load trust master');
      navigate('/trust-masters');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    showSuccess('Trust master updated successfully');
    navigate(`/trust-masters/${id}`);
  };

  const handleCancel = () => {
    navigate(`/trust-masters/${id}`);
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!trustMaster) {
    return null;
  }

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <IconButton
          onClick={handleCancel}
          sx={{
            color: colors.text.secondary,
            mr: 2,
            '&:hover': {
              backgroundColor: colors.action.hover,
              color: colors.primary.main,
            },
          }}
        >
          <FiArrowLeft size={24} />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Edit Trust
        </Typography>
      </Box>

      {/* Form */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '4px',
          border: `1px solid ${colors.border.main}`,
          backgroundColor: colors.background.paper,
        }}
      >
        <TrustMasterForm trustMaster={trustMaster} onSave={handleSave} onCancel={handleCancel} />
      </Paper>
    </Box>
  );
}
